import { useEffect, useState } from "react";
import { supabase } from "./supabase.js";

/** The stages a job moves through (migration 0085), in order. The names are fixed; the labels are what the portal shows. */
export const JOB_STAGES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "inspection_booked", label: "Inspection booked" },
  { value: "quoted", label: "Quoted" },
  { value: "won", label: "Won" },
  { value: "scheduled", label: "Scheduled" },
  { value: "done", label: "Done" },
  { value: "lost", label: "Lost" },
] as const;

export type JobStage = (typeof JOB_STAGES)[number]["value"];

/** Stages that still need the owner's attention (the default list). */
export const OPEN_STAGES: readonly JobStage[] = ["new", "contacted", "inspection_booked", "quoted", "won", "scheduled"];

export const JOB_URGENCIES = [
  { value: "emergency", label: "Emergency" },
  { value: "urgent", label: "Urgent" },
  { value: "routine", label: "Routine" },
] as const;

export type JobUrgency = (typeof JOB_URGENCIES)[number]["value"];

export function stageLabel(stage: string): string {
  return JOB_STAGES.find((s) => s.value === stage)?.label ?? stage;
}

export function urgencyLabel(urgency: string): string {
  return JOB_URGENCIES.find((u) => u.value === urgency)?.label ?? urgency;
}

/** Emergencies first, then urgent, then routine; within each, the newest first. */
export function sortJobs<T extends { urgency: string; created_at: string }>(jobs: readonly T[]): T[] {
  const rank: Record<string, number> = { emergency: 0, urgent: 1, routine: 2 };
  return [...jobs].sort((a, b) => (rank[a.urgency] ?? 3) - (rank[b.urgency] ?? 3) || Date.parse(b.created_at) - Date.parse(a.created_at));
}

/** The quote amount as dollars for display ("$8,500"), or "-" when none. */
export function formatQuote(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "-";
  return `$${(cents / 100).toLocaleString("en-AU", { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}

/** Dollars typed in a form ("8500", "$8,500.50") as whole cents; null when blank, undefined when it is not a valid amount. */
export function quoteToCents(text: string): number | null | undefined {
  const cleaned = text.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return undefined;
  return Math.round(Number(cleaned) * 100);
}

/** Whether the business has Leads and jobs switched on (businesses.jobs_enabled, migration 0085). Null while loading. */
export function useJobsEnabled(businessId: string | null): boolean | null {
  const [state, setState] = useState<{ businessId: string | null; enabled: boolean | null }>({ businessId: null, enabled: null });
  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    void supabase
      .from("businesses")
      .select("jobs_enabled")
      .eq("id", businessId)
      .single()
      .then(({ data }: { data: { jobs_enabled: boolean } | null }) => {
        if (!cancelled) setState({ businessId, enabled: data ? data.jobs_enabled === true : false });
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);
  return state.businessId === businessId ? state.enabled : null;
}
